import { useEffect, useState } from "react";

import type { RawParams } from "@uirouter/core";

import { proNavigator } from "single-spa/react-app/ProNavigator";
import { router } from "single-spa/react-app/router/router";

type StateNameAndParams = {
    name: string;
    params: RawParams;
};

type IsPreventLeavePage = boolean | (() => boolean);

const initialState = {
    name: "",
    params: {},
};

const resolveIsPreventLeavePage = (isPreventLeavePage: IsPreventLeavePage): boolean =>
    typeof isPreventLeavePage === "function" ? isPreventLeavePage() : isPreventLeavePage;

const usePreventLeavePage = (
    isPreventLeavePage: IsPreventLeavePage,
    actionBeforeLeavePage: () => void,
    skipStates: string[],
    ownTargetState?: { name: string; params: Record<string, string> },
): {
    setConfirm: (status: boolean) => void;
    resetRedirectToState: () => void;
} => {
    const [isConfirm, setConfirm] = useState(false);
    const [toState, setToState] = useState<StateNameAndParams>(initialState);

    const resetRedirectToState = () => setToState(initialState);

    useEffect(() => {
        const onBefore = router.transitionService.onExit({}, (transition) => {
            const name = transition.$to().name;
            const params = transition.params();
            if (skipStates.includes(name)) {
                return true;
            }

            if (isConfirm) {
                return true;
            }

            setToState({ name, params });

            if (resolveIsPreventLeavePage(isPreventLeavePage)) {
                actionBeforeLeavePage();
                return false;
            }

            return true;
        });
        return () => {
            onBefore();
        };
    }, [isConfirm, isPreventLeavePage]);

    useEffect(() => {
        if (!isConfirm) {
            return;
        }

        if (toState.name.length !== 0) {
            proNavigator.go(toState.name, toState.params);
            return;
        }

        if (ownTargetState?.name) {
            proNavigator.go(ownTargetState.name, ownTargetState.params);
            return;
        }
    }, [isConfirm]);

    return { setConfirm, resetRedirectToState };
};

export default usePreventLeavePage;
